import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  getGamesList,
  handlePaginateGames,
  handleSelectAllGames,
  handleSelectItemGames,
} from "store/liveOps/wagers/slice";
import { gamesListLimit, gamesListLimits } from "configs/limits";
import { ModalTypes } from "configs/types";
import { openModal } from "store/modals/slice";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import { FilterItemTypes } from "configs/types";

import ToggleBtn from "components/buttons/ToggleBtn";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import Filter from "components/global/Filter/Filter";
import CopyToClipboard from "components/common/CopyToClipboard/CopyToClipboard";
import editIcon from "assets/icons/edit-icon.svg";
import listIcon from "assets/icons/list-icon.svg";
import gridIcon from "assets/icons/grid-icon.svg";
import { Table } from "components/table/Table";
import { GameItem } from "store/gameCenter/games/slice";
import EmptyState from "components/EmptyState/EmptyState";
import Pagination from "components/Pagination";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import ImageWithFallback from "helpers/ImageWithFallback";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import eyeIcon from "assets/icons/eye.svg";
import { RefreshButton } from "components/buttons/RefreshButton";
import { useRefreshList } from "helpers/useRefreshList";

const NotProcessing: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [activeView, setView] = useState<string>("table");
  const [currentLimit, setCurrentLimit] = useState(gamesListLimit);
  const [search, setSearch] = useState<string>("");
  const [providersSelected, setProvidersSelected] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [providers, setProviders] = useState<
    { value: string | number; label: string }[]
  >([]);
  const options = [
    {
      label: t("addToWagerList"),
      value: "addToWagerList",
    },
    {
      label: t("hide"),
      value: "hide",
    },
    {
      label: t("disable"),
      value: "disable",
    },
  ];

  const gamesList = useAppSelector((store) => store.wagers.gamesList);
  const isListFetched = useAppSelector(
    (store) => store.wagers.isListGamesFetched,
  );
  const totalItems = useAppSelector((store) => store.wagers.totalItemsGames);
  const totalPages = useAppSelector((store) => store.wagers.totalPagesGames);
  const activePage = useAppSelector((store) => store.wagers.activePageGames);
  const processingGameItemId = useAppSelector(
    (store) => store.wagers.processingGameItemId,
  );
  const selectedItems = useAppSelector(
    (store) => store.wagers.selectedItemsGames,
  );
  const permissions = usePermissionHelper();
  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'wagers',
    isListFetched,
    field: 'isListGamesFetched'
  });

  const getProviders = async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.providersList}?limit=500&offset=0`,
      );
      const array =
        result.data.items?.map((el: any) => {
          return {
            value: el.id,
            label: el.title,
          };
        }) ?? [];
      setProviders(array);
    } catch (error) {
      console.log(error, "error");
    }
  };
  useEffect(() => {
    getProviders();
  }, []);

  useEffect(() => {
    if (!isListFetched) {
      dispatch(
        getGamesList({
          limit: currentLimit,
          offset: activePage - 1,
          providerIds: providersSelected.map((el) => el.value as number),
          gameTitle: search === "" ? undefined : search,
        }),
      );
    }
  }, [activePage, providersSelected, isListFetched, currentLimit]);

  const handleAllClick = () => {
    dispatch(handleSelectAllGames());
  };
  const handleItemClick = (id: number) => {
    dispatch(handleSelectItemGames(id));
  };
  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginateGames(activePage - 1));
    }
  };
  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginateGames(activePage + 1));
    }
  };

  const handleActionClick = (e: string) => {
    if (e === "addToWagerList" && selectedItems.length > 0) {
      dispatch(
        openModal({
          activeModal: ModalTypes.addGamesToWager,
        }),
      );
    }
  };

  const handleOpenEditModal = (id: number) => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.editGame}&entityId=${id}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.editGame }));
  };

  const handleApply = () => {
    dispatch(handlePaginateGames(1));
  };
  const handleClearFilters = () => {
    setSearch("");
    setProvidersSelected([]);
    handleApply();
  };
  const handleSearch = (e: string) => {
    setSearch(e);
  };
  const handleApplyProviders = (
    e: { value: string | number; label: string }[],
    isDelete?: boolean,
  ) => {
    setProvidersSelected(isDelete ? e : [...providersSelected, ...e]);
  };

  const actionProps = permissions?.update
  ? {
      onEdit: (id: number) => handleOpenEditModal(id),
      onView: undefined,
    }
  : {
      onEdit: undefined,
      onView: (id: number) => handleOpenEditModal(id),
    };

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <Filter
        className="wager-games-filter"
        handleClear={handleClearFilters}
        handleApply={handleApply}
        filterItems={[
          {
            type: FilterItemTypes.input,
            title: t("title"),
            boxTitle: t("title"),
            icon: gridIcon,
            inputValue: search,
            setInputValue: handleSearch,
          },
          {
            type: FilterItemTypes.multiselect,
            title: t("providers"),
            boxTitle: t("filterProvider"),
            icon: gridIcon,
            itemsArray: providers,
            activeItems: providersSelected,
            handleApply: handleApplyProviders,
            disabled: providers.length === 0,
          },
        ]}
      />
      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
          <div className="page-list-header--box">
            <ToggleBtn
              handleClick={(v) => setView(v)}
              activeValue={activeView}
              disabled={gamesList.length === 0}
              icons={[
                {
                  url: listIcon,
                  value: "table",
                },
                {
                  url: gridIcon,
                  value: "grid",
                },
              ]}
            />
            <DefaultDropdown
              className="default-dropdown--sentence-case"
              onSelect={(e) => {
                handleActionClick(e as string);
              }}
              placeholder={t("chooseAction")}
              options={options}
              isSmall
              disabled={selectedItems.length === 0 || !permissions?.update}
            />
          </div>
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={7} rowsCount={12} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && (
          <>
            <div
              className={`game-table-wrap ${activeView === "table" ? "" : "grid"}`}
            >
              {gamesList.length === 0 && (
                <EmptyState message={t("noNotProcessingGamesFound")} />
              )}

              {gamesList.length > 0 && (
                <>
                  {activeView === "table" ? (
                    <Table
                      data={
                        gamesList as Array<GameItem & { devices: string[] }>
                      }
                      idField="id"
                      activeRowId={searchParams.get("entityId")}
                      processingId={processingGameItemId}
                      selectedItems={selectedItems}
                      onAllSelect={handleAllClick}
                      onSelect={(id) => handleItemClick(id)}
                      hasDelete={false}
                      hasEdit={permissions?.update}
                      hasView={permissions?.read && !permissions?.update}
                      customColumns={(row) => ({
                        title: (
                          <div className="copyable-title-cell">
                            <CopyToClipboard text={row.title} />
                            <span>{row.title}</span>
                          </div>
                        ),
                      })}
                      {...actionProps}
                    />
                  ) : (
                    <div className="game-grid">
                      {gamesList.map((el) => (
                        <div className="game-item" key={el.id}>
                          <div className="game-item-img">
                            <ImageWithFallback
                              src={el.image as string}
                              alt={el.title}
                              className="game-item-img__image"
                            />
                          </div>
                          <div className="game-item-content">
                            <div className="game-item-content--title">
                              {el.title}
                            </div>
                            <div className="game-item-content--actions">
                              <div
                                onClick={() => handleItemClick(el.id)}
                                className={`def-check-control ${
                                  selectedItems.includes(el.id) ? "checked" : ""
                                }`}
                              />
                              <div className="provider">
                                {el.provider.title}
                              </div>
                              <div
                                className="option"
                                onClick={() => handleOpenEditModal(el.id)}
                              >
                                <img src={permissions?.update ? editIcon : eyeIcon} alt="" />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {gamesList.length > 0 && totalPages && (
              <Pagination
                activePage={activePage}
                limit={currentLimit}
                totalItems={totalItems}
                totalPages={totalPages}
                limitsArray={gamesListLimits}
                handleLimitClick={(limit) => {
                  setCurrentLimit(limit);
                  dispatch(handlePaginateGames(1));
                }}
                handleNextClick={handleNextClick}
                handlePrevClick={handlePrevClick}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NotProcessing;
